import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { getCountryNameByCode } from '@src/constants/countries';
import { useSubmitNifOrderIntakeMutation } from '@src/generated/graphql';
import { useToast } from '@src/hooks/useToast';
import { DashboardPageNavigationState } from '@src/pages/dashboard/dashboard.page';
import { formatError } from '@src/services/error-formatter';
import { logError } from '@src/services/error-logger';
import OrderNavigationButtons from '../../components/OrderNavigationButtons';
import ReviewList, { ReviewListItem } from '../../components/ReviewList';
import {
  GENDER_OPTIONS,
  MARITAL_STATUS_OPTIONS,
} from '../../constants/options';
import { NifOrderStepPageProps } from '../types/stepProps';

export default function NifReviewPage({
  nifOrder,
  previousUrl,
}: NifOrderStepPageProps): JSX.Element {
  const [submitNifIntake, { loading: isSubmittingNifIntake }] =
    useSubmitNifOrderIntakeMutation();
  const toast = useToast();
  const { t, i18n } = useTranslation(['nif']);
  const navigate = useNavigate();

  const submitNifIntakeAndNavigate = useCallback(async () => {
    try {
      await submitNifIntake({
        variables: {
          input: {
            nifOrderId: nifOrder.id,
          },
        },
      });
      void navigate('/', {
        state: {
          showIntakeSuccess: true,
        } as DashboardPageNavigationState,
      });
    } catch (err) {
      toast.error(formatError(err, 'Error submitting NIF intake.'));
      logError(err);
    }
  }, [nifOrder, toast, submitNifIntake, navigate]);

  const personalDetailItems: ReviewListItem[] = [
    {
      label: t('order_review.full_name_label', 'Full name'),
      value: `${nifOrder.givenNames} ${nifOrder.surname}`,
    },
    {
      label: t('order_review.birthday_label', 'Date of birth'),
      value: nifOrder.birthDate,
    },
    {
      label: t('order_review.birth_country_label', 'Country of birth'),
      value: getCountryNameByCode(nifOrder.birthCountry, i18n.language),
    },
    {
      label: t('order_review.doc_number_label', 'Document number'),
      value: nifOrder.documentNumber,
    },
    {
      label: t('order_review.doc_country_label', 'Document country'),
      value: getCountryNameByCode(
        nifOrder.documentIssuingCountry,
        i18n.language,
      ),
    },
    {
      label: t('order_review.doc_expiry_label', 'Document expiration date'),
      value: nifOrder.documentExpirationDate,
    },
  ];

  const manualT = t;
  if (nifOrder.gender) {
    personalDetailItems.push({
      label: t('order_review.sex', 'Sex'),
      value: manualT(GENDER_OPTIONS[nifOrder.gender]),
    });
  }

  if (nifOrder.maritalStatus) {
    personalDetailItems.push({
      label: t('order_review.marital_status', 'Marital Status'),
      value: manualT(MARITAL_STATUS_OPTIONS[nifOrder.maritalStatus]),
    });
  }

  const { address, parents } = nifOrder;

  const parentItems: ReviewListItem[] | null =
    parents.length === 2
      ? [
          {
            label: t(
              'order_review.parent_name_label',
              'Parent {{n}} full name',
              { n: 1 },
            ),
            value: `${parents[0].givenNames} ${parents[0].surname}`,
          },
          {
            label: t(
              'order_review.parent_doc_number_label',
              'Parent {{n}} passport number',
              { n: 1 },
            ),
            value: parents[0].documentNumber,
          },
          {
            label: t(
              'order_review.parent_name_label',
              'Parent {{n}} full name',
              { n: 2 },
            ),
            value: `${parents[1].givenNames} ${parents[1].surname}`,
          },
          {
            label: t(
              'order_review.parent_doc_number_label',
              'Parent {{n}} passport number',
              { n: 2 },
            ),
            value: parents[1].documentNumber,
          },
        ]
      : null;

  const residentialAddressItems: ReviewListItem[] | null | undefined =
    address && [
      {
        label: t('order_review.address_street_label', 'Street address'),
        value: `${address.lineOne}${
          address.lineTwo ? `\n${address.lineTwo}` : ''
        }`,
      },
      {
        label: t('order_review.address_city_label', 'City / town'),
        value: address.city,
      },
      {
        label: t('order_review.address_region_label', 'State or province'),
        value: address.region || '',
      },
      {
        label: t('order_review.address_postal_label', 'Postal code'),
        value: address.postalCode,
      },
      {
        label: t('order_review.address_country_label', 'Country'),
        value: getCountryNameByCode(address.country, i18n.language),
      },
    ];

  return (
    <div className="space-y-8">
      <div className="space-y-2">
        <h2>{t('order_review.title', 'Review')}</h2>
        <p>
          {t(
            'order_review.instructions',
            'Please take one last look to make sure everything looks correct.',
          )}
        </p>
      </div>

      <ReviewList
        title={t('order_review.personal_details_title', 'Personal details')}
        updateLink="../personal-details"
        items={personalDetailItems}
      />

      {parentItems && (
        <ReviewList
          title={t(
            'order_review.parents_information_title',
            "Parents' information",
          )}
          updateLink="../parents-information"
          items={parentItems}
        />
      )}

      {residentialAddressItems && (
        <ReviewList
          title={t('order_review.residential_title', 'Residential address')}
          updateLink="../residential-address"
          items={residentialAddressItems}
        />
      )}

      <OrderNavigationButtons
        previousUrl={previousUrl}
        nextText={t('buttons.submit', 'Submit', { ns: 'translation' })}
        disabled={isSubmittingNifIntake}
        onClick={submitNifIntakeAndNavigate}
      />
    </div>
  );
}
